import React, { useEffect } from "react";
import { TabsContent } from "../../components/misc/Tabs";
import { Label } from "../../components/misc/Label";
import { Input } from "../../components/misc/Input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import { Switch } from "../../components/misc/Switch";
import { MLS, Plan, User, UserRole } from "../types";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import LoadingScreen from "../../screens/LoadingScreen";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import SimpleTooltip from "../../components/misc/SimpleTooltip";
import { CornerDownRight, Info } from "lucide-react";
import { deriveContentSimulationToggle } from "./simulationToggles.util";

interface EditUserOverlayProps {
    userData: Partial<User> | null;
    setUserData: React.Dispatch<React.SetStateAction<Partial<User> | null>>;
    teamAdmins: User[];
    isAdd: boolean;
    allPlans: Plan[];
}

const EditUserOverlay: React.FC<EditUserOverlayProps> = ({
    userData,
    setUserData,
    teamAdmins,
    isAdd,
    allPlans,
}) => {
    const [mls, setMls] = React.useState<MLS[] | null>(null);
    const [loading, setLoading] = React.useState(false);
    const isMobile = useIsMobile(900);
    const rowClass = isMobile ? "flex flex-col gap-1" : "grid grid-cols-4 items-center gap-4";
    const labelClass = isMobile ? "" : "text-right";
    const inputClass = isMobile ? "w-full" : "col-span-3";
    const selectClass = isMobile ? "w-full" : "col-span-3";
    const switchRowClass = isMobile ? "flex flex-row items-center gap-2" : "grid grid-cols-4 items-center gap-4";
    const switchLabelClass = isMobile ? "flex items-center gap-1" : "flex items-center justify-end gap-1 text-right";
    const hintIconClass = "h-4 w-4 shrink-0 text-gray-500";

    useEffect(() => {
        const fetchMls = async () => {
            setLoading(true);

            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                const mlsResponse = await axios.get(
                    `${backendUrl}/onboarding/mls-options`,
                    {
                        headers,
                    }
                );

                if (mlsResponse.status !== 200) {
                    throw new Error("Failed to fetch MLS");
                }

                setMls(mlsResponse.data);
            } catch (error: unknown) {
                console.error("Failed to fetch MLS");
                toast.error(
                    <CustomToast message="Failed to fetch MLS" type="error" />,
                    {
                        autoClose: 3000,
                    }
                );
            } finally {
                setLoading(false);
            }
        };

        fetchMls();
    }, []);

    if (loading) {
        return <LoadingScreen />;
    }

    if (!userData) {
        return null;
    }

    const contentSimulation = deriveContentSimulationToggle(userData);

    return (
        <TabsContent value="userData">
            <div className="grid gap-4 py-4">
                {/* First Name */}
                <div className={rowClass}>
                    <Label htmlFor="nameFirst" className={labelClass}>
                        First Name
                    </Label>
                    <Input
                        id="nameFirst"
                        value={userData.nameFirst || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                nameFirst: e.target.value,
                            })
                        }
                        className={inputClass}
                        required
                    />
                </div>
                {/* Last Name */}
                <div className={rowClass}>
                    <Label htmlFor="nameLast" className={labelClass}>
                        Last Name
                    </Label>
                    <Input
                        id="nameLast"
                        value={userData.nameLast || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                nameLast: e.target.value,
                            })
                        }
                        className={inputClass}
                        required
                    />
                </div>
                {/* Email */}
                <div className={rowClass}>
                    <Label htmlFor="email" className={labelClass}>
                        Email
                    </Label>
                    <Input
                        type="email"
                        id="email"
                        value={userData.email || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                email: e.target.value,
                            })
                        }
                        className={inputClass}
                        required
                    />
                </div>
                {/* Role — provisioning derives role/onboarded from the plan,
                    so these are edit-only to avoid collecting input the
                    provision endpoint ignores. */}
                {!isAdd && (
                <>
                <div className={rowClass}>
                    <Label htmlFor="role" className={labelClass}>
                        Role
                    </Label>
                    <Select
                        onValueChange={(value) =>
                            setUserData({
                                ...userData,
                                role: value as UserRole,
                            })
                        }
                        value={userData.role || UserRole.USER}
                    >
                        <SelectTrigger className={selectClass}>
                            <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={UserRole.USER}>User</SelectItem>
                            <SelectItem value={UserRole.TEAM_ADMIN}>
                                Team Admin
                            </SelectItem>
                            <SelectItem value={UserRole.TEAM_USER}>
                                Team User
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {/* Main Account (Conditional) */}
                {userData.role === UserRole.TEAM_USER && (
                    <div className={rowClass}>
                        <Label htmlFor="mainAccount" className={labelClass}>
                            Main Account
                        </Label>
                        <Select
                            onValueChange={(value) =>
                                setUserData({
                                    ...userData,
                                    accountId: value,
                                })
                            }
                            value={userData.accountId || ""}
                        >
                            <SelectTrigger className={selectClass}>
                                <SelectValue placeholder="Select a Team Admin" />
                            </SelectTrigger>
                            <SelectContent>
                                {teamAdmins.map((admin) => (
                                    <SelectItem key={admin.id} value={admin.id}>
                                        {admin.nameFirst} {admin.nameLast}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                </>
                )}
                {/* MLS Approved */}
                <div className={switchRowClass}>
                    <Label htmlFor="mlsApproved" className={labelClass}>
                        MLS Approved
                    </Label>
                    <Switch
                        id="mlsApproved"
                        checked={userData.mlsApproved || false}
                        onCheckedChange={(checked) =>
                            setUserData({
                                ...userData,
                                mlsApproved: checked,
                            })
                        }
                    />
                </div>
                {/* Onboarded */}
                {!isAdd && (
                <>
                <div className={switchRowClass}>
                    <Label htmlFor="onboarded" className={labelClass}>
                        Onboarded
                    </Label>
                    <Switch
                        id="onboarded"
                        checked={userData.onboarded || false}
                        onCheckedChange={(checked) =>
                            setUserData({
                                ...userData,
                                onboarded: checked,
                            })
                        }
                    />
                </div>
                {/* MB-1399: Developer Testing Mode — posts publish through the
                    whole pipeline but never reach Ayrshare (simulated). */}
                <div className={switchRowClass}>
                    <div className={switchLabelClass}>
                        <Label htmlFor="simulatePosting">
                            Developer Testing Mode
                        </Label>
                        <SimpleTooltip
                            message="Simulate posting - posts run through the whole pipeline but are never sent to the social platforms."
                            visible={true}
                            className="text-left"
                        >
                            <Info className={hintIconClass} />
                        </SimpleTooltip>
                    </div>
                    <Switch
                        id="simulatePosting"
                        checked={userData.simulatePosting || false}
                        onCheckedChange={(checked) =>
                            setUserData({
                                ...userData,
                                simulatePosting: checked,
                            })
                        }
                    />
                </div>
                {/* MB-1491: nested under Developer Testing Mode — the api
                    guard requires both flags, so with the parent off this is
                    inert: disabled here, but the stored value never clears. */}
                <div
                    className={cn(
                        switchRowClass,
                        contentSimulation.disabled && "opacity-50"
                    )}
                >
                    <div className={switchLabelClass}>
                        <Label htmlFor="simulateContentGeneration">
                            <CornerDownRight
                                className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom text-gray-500"
                                aria-hidden
                            />
                            Simulate Content Generation
                        </Label>
                        <SimpleTooltip
                            message="Skip OpenAI - placeholder content is generated instead of real AI content. Requires Developer Testing Mode."
                            visible={true}
                            className="text-left"
                        >
                            <Info className={hintIconClass} />
                        </SimpleTooltip>
                    </div>
                    <Switch
                        id="simulateContentGeneration"
                        checked={contentSimulation.checked}
                        disabled={contentSimulation.disabled}
                        onCheckedChange={(checked) =>
                            setUserData({
                                ...userData,
                                simulateContentGeneration: checked,
                            })
                        }
                    />
                </div>
                {/* License Number */}
                <div className={rowClass}>
                    <Label htmlFor="licenseNumber" className={labelClass}>
                        License Number
                    </Label>
                    <Input
                        id="licenseNumber"
                        value={userData.licenseNumber || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                licenseNumber: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                </>
                )}
                {/* Company Name */}
                <div className={rowClass}>
                    <Label htmlFor="companyName" className={labelClass}>
                        Company Name
                    </Label>
                    <Input
                        id="companyName"
                        value={userData.companyName || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                companyName: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                {/* Phone Number */}
                {!isAdd && (
                <>
                <div className={rowClass}>
                    <Label htmlFor="phoneNumber" className={labelClass}>
                        Phone Number
                    </Label>
                    <Input
                        id="phoneNumber"
                        value={userData.phoneNumber || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                phoneNumber: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                {/* Broker */}
                <div className={rowClass}>
                    <Label htmlFor="broker" className={labelClass}>
                        Broker
                    </Label>
                    <Input
                        id="broker"
                        value={userData.broker || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                broker: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                {/* Broker License */}
                <div className={rowClass}>
                    <Label htmlFor="brokerLicense" className={labelClass}>
                        Broker License
                    </Label>
                    <Input
                        id="brokerLicense"
                        value={userData.brokerLicense || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                brokerLicense: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                {/* Broker Email */}
                <div className={rowClass}>
                    <Label htmlFor="brokerEmail" className={labelClass}>
                        Broker Email
                    </Label>
                    <Input
                        type="email"
                        id="brokerEmail"
                        value={userData.brokerEmail || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                brokerEmail: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
                </>
                )}
                {/* MLS or Other MLS */}
                {!userData?.otherMLS || userData?.otherMLS?.length <= 0 ? (
                    <div className={rowClass}>
                        <Label htmlFor="mls" className={labelClass}>
                            MLS
                        </Label>
                        <Select
                            onValueChange={(value) =>
                                setUserData({
                                    ...userData,
                                    mls: value as string,
                                })
                            }
                            value={
                                typeof userData.mls === "string"
                                    ? userData.mls
                                    : userData.mls?._id.toString() || ""
                            }
                        >
                            <SelectTrigger className={selectClass}>
                                <SelectValue placeholder="Select a MLS" />
                            </SelectTrigger>
                            <SelectContent>
                                {mls?.map((m) => (
                                    <SelectItem
                                        key={m._id.toString()}
                                        value={m._id.toString()}
                                    >
                                        {m.description}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : (
                    <div className={rowClass}>
                        <Label htmlFor="otherMLS" className={labelClass}>
                            Other MLS
                        </Label>
                        <Input
                            id="otherMLS"
                            value={userData.otherMLS || ""}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    otherMLS: e.target.value,
                                })
                            }
                            className={inputClass}
                        />
                    </div>
                )}
                {userData.plan ? (
                    <div className={rowClass}>
                        <Label htmlFor="plan" className={labelClass}>
                            Plan
                        </Label>
                        <Select
                            onValueChange={(value) =>
                                setUserData({
                                    ...userData,
                                    plan: allPlans.find(
                                        (plan: Plan) => plan.id === value
                                    ) as Plan,
                                })
                            }
                            value={userData.plan.id || ""}
                        >
                            <SelectTrigger className={selectClass}>
                                <SelectValue placeholder="Select a Plan" />
                            </SelectTrigger>
                            <SelectContent>
                                {allPlans.map((plan) => (
                                    <SelectItem
                                        key={plan.id}
                                        value={plan.id || ""}
                                    >
                                        {plan.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}
                {/* Time Zone */}
                <div className={rowClass}>
                    <Label htmlFor="timeZone" className={labelClass}>
                        Time Zone
                    </Label>
                    <Input
                        id="timeZone"
                        value={userData.timeZone || ""}
                        onChange={(e) =>
                            setUserData({
                                ...userData,
                                timeZone: e.target.value,
                            })
                        }
                        className={inputClass}
                    />
                </div>
            </div>
        </TabsContent>
    );
};

export default EditUserOverlay;
